const CAP53 = {
  "id": 217,
  "title": "Liquid y RSK",
  "slug": "liquid-y-rsk",
  "type": "lesson",
  "category": "bitcoin-avanzado",
  "level": "intermediate",
  "preview": "Liquid y RSK son las sidechains más importantes del ecosistema Bitcoin. Ambas amplían sus capacidades sin modificar la cadena principal, permitiendo funciones financieras avanzadas y contratos inteligentes.",
  "sections": [
    {
      "heading": "Introducción",
      "text": [
        "Bitcoin fue diseñado para ser un sistema seguro, simple y resistente. Aunque cumple su objetivo como reserva digital de valor y sistema de transferencias, la cadena principal no incorpora funciones avanzadas como contratos inteligentes complejos, emisión de tokens o transacciones con privacidad adicional.",
        "Para ampliar las capacidades de Bitcoin sin comprometer su diseño base, surgieron las sidechains. Entre ellas destacan Liquid Network y RSK (Rootstock), dos proyectos que agregan nuevas funcionalidades manteniendo un vínculo directo con Bitcoin."
      ]
    },

    {
      "heading": "¿Qué son las sidechains?",
      "text": [
        "Una sidechain es una cadena de bloques independiente que está conectada a Bitcoin mediante un sistema de transferencia bidireccional (peg).",
        "Este mecanismo permite mover BTC desde la cadena principal hacia otra cadena, donde adquieren otra representación, para utilizar nuevas funciones sin modificar el protocolo original.",
        "Después, esos BTC pueden regresar a la cadena principal en cualquier momento, manteniendo la equivalencia 1:1.",
        "Las sidechains permiten experimentar e innovar sin riesgo directo para la seguridad de la cadena principal de Bitcoin."
      ]
    },

    {
      "heading": "Liquid Network",
      "text": [
        "1. ¿Qué es Liquid Network?",
        "Liquid Network es una sidechain desarrollada por Blockstream enfocada en mejorar la eficiencia de pagos, la privacidad y la emisión de activos digitales.",
        "Los BTC enviados a Liquid se transforman en L-BTC, un activo respaldado 1:1 que puede circular dentro de la red Liquid.",
        "",
        "2. Características principales de Liquid:",
        "- Liquidaciones rápidas: los bloques son generados cada minuto, mucho más rápido que en Bitcoin.",
        "- Privacidad adicional: Liquid utiliza Confidential Transactions, ocultando montos y tipos de activos en las transacciones.",
        "- Emisión de activos: permite crear stablecoins, tokens de seguridad y otros activos financieros sobre la misma infraestructura.",
        "- Enfoque corporativo: está pensada para exchanges, brokers, empresas y usuarios avanzados que requieren eficiencia y confidencialidad.",
        "- Peg bidireccional federado: los BTC se convierten en L-BTC mediante una red de entidades denominadas functionaries, que custodian y validan el peg.",
        "",
        "3. Casos de uso de Liquid:",
        "- Transferencias rápidas y eficientes entre exchanges y plataformas de trading.",
        "- Movimientos privados de grandes volúmenes de capital.",
        "- Tokenización de activos del mundo real (bonos, acciones, etc.).",
        "- Emisión y circulación de stablecoins sobre Bitcoin.",
        "- Gestión de liquidez financiera en mercados globales con menor fricción."
      ]
    },

    {
      "heading": "RSK (Rootstock)",
      "text": [
        "1. ¿Qué es RSK?",
        "RSK es una sidechain compatible con la Máquina Virtual de Ethereum (EVM), lo que permite ejecutar contratos inteligentes escritos en Solidity dentro del ecosistema Bitcoin.",
        "Su objetivo principal es traer funcionalidad de smart contracts a Bitcoin, manteniendo un alto nivel de seguridad gracias al merged mining, que permite que los mineros de Bitcoin protejan la red RSK reutilizando su potencia de hash.",
        "",
        "2. RBTC: el puente hacia Bitcoin",
        "Al enviar BTC hacia RSK, estos se convierten en RBTC, la moneda nativa de la red, respaldada 1:1 con BTC.",
        "RBTC se utiliza para ejecutar contratos inteligentes, interactuar con dApps y pagar comisiones dentro del ecosistema RSK.",
        "",
        "3. Características principales de RSK:",
        "- Smart contracts avanzados: compatibles con Solidity y con muchas de las herramientas utilizadas en Ethereum.",
        "- Merged mining: la mayoría de los mineros de Bitcoin pueden participar en la seguridad de RSK sin coste adicional de hardware.",
        "- Transferencias rápidas: produce bloques aproximadamente cada 30 segundos, lo que mejora la experiencia de uso de las dApps.",
        "- Tokenización y DeFi: permite construir aplicaciones financieras, protocolos DeFi y otros servicios sobre una base vinculada a Bitcoin.",
        "- Peg bidireccional: similar a Liquid, pero con una arquitectura de seguridad distinta basada en su propia federación y en el merged mining.",
        "",
        "4. Casos de uso de RSK:",
        "- Aplicaciones DeFi sobre Bitcoin (préstamos, intercambios, derivados, etc.).",
        "- Servicios de identidad digital descentralizada.",
        "- Creación de DAOs basadas en contratos inteligentes.",
        "- Tokenización de activos del mundo real y emisión de tokens programables.",
        "- Automatización de pagos y lógicas financieras mediante smart contracts."
      ]
    },

    {
      "heading": "Comparación entre Liquid y RSK",
      "table": {
        "columns": [
          "Característica",
          "Liquid Network",
          "RSK (Rootstock)"
        ],
        "rows": [
          [
            "Objetivo principal",
            "Pagos rápidos, privados y emisión de activos.",
            "Contratos inteligentes y aplicaciones descentralizadas."
          ],
          [
            "Representación del BTC",
            "L-BTC",
            "RBTC"
          ],
          [
            "Tiempo de bloque",
            "1 minuto",
            "30 segundos"
          ],
          [
            "Privacidad",
            "Alta — Confidential Transactions.",
            "Estándar — modelo similar al de Ethereum, sin ocultar montos por defecto."
          ],
          [
            "Seguridad",
            "Federación de functionaries que gestionan el peg y la validación de bloques.",
            "Merged mining con mineros de Bitcoin que reutilizan su potencia de hash."
          ],
          [
            "Casos de uso típicos",
            "Transferencias financieras, stablecoins, tokens corporativos y gestión de liquidez.",
            "DeFi, dApps, contratos inteligentes complejos y ecosistemas programables."
          ]
        ]
      }
    },

    {
      "heading": "Beneficios de las sidechains para Bitcoin",
      "text": [
        "Liquid y RSK amplían las capacidades de Bitcoin sin modificar su diseño base ni comprometer la seguridad de la capa principal.",
        "Entre los beneficios principales se encuentran:",
        "- Innovación paralela: la experimentación se realiza fuera de la cadena principal, reduciendo riesgos para Bitcoin.",
        "- Mayor funcionalidad: contratos inteligentes, privacidad avanzada y emisión de activos financieros.",
        "- Escalabilidad adicional: operaciones más rápidas, especializadas y eficientes en cada sidechain.",
        "- Atracción de capital institucional: especialmente gracias a Liquid y su enfoque en mercados financieros regulados.",
        "- Ecosistemas completos sobre Bitcoin: con RSK y sus dApps, se construyen servicios financieros y aplicaciones complejas apoyadas en BTC."
      ]
    },

    {
      "heading": "Conclusión",
      "text": [
        "Liquid Network y RSK representan dos caminos complementarios para expandir las capacidades de Bitcoin.",
        "Liquid aporta eficiencia financiera, rapidez en liquidaciones y mayor privacidad para la emisión y gestión de activos.",
        "RSK introduce contratos inteligentes compatibles con Ethereum, permitiendo que Bitcoin sea la base de aplicaciones descentralizadas y protocolos DeFi.",
        "Ambas tecnologías permiten que Bitcoin evolucione manteniendo su base sólida y descentralizada, y son herramientas clave para entender hacia dónde se dirige la infraestructura financiera global basada en Bitcoin."
      ]
    }
  ]
};

export default CAP53;
